import React, { useEffect, useMemo, useState } from "react";
import axios from "../../api/axios";
import { getAttributeValue, getPlanExecutionStats } from "../../utils/utils";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";
import APRComment from "./APRComment.js/AprComments";
import APRmemo from "./APRComment.js/APRmemo";
import AprViewToggle, { isBlankDimension } from "./AprViewToggle";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const AAP_YEAR_ATTRIBUTE = "pWkYqcKukAY";
const AAP_PROGRAM = "ArLnAxhykoz";
const PROJECTS_PROGRAMMES_PROGRAM = "g3wMUKEMmH3";
const PROJECTS_STATUS_ELEMENT = "tE3QKB203nh";

const DIMENSION_ORDER = [
  "Economic Development (ED)",
  "Social Development (SD)",
  "Implementation, Coordination, Monitoring And Evaluation",
  "Governance, Corruption And Public Accountability(GCPA)",
  "Environment, infrastructure and human settlement",
  "Emergency Planning And Covid-19 Response",
  "Total",
];

function isOtherDdDimension(dimension) {
  const normalized = String(dimension || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  return (
    normalized === "other dd" ||
    normalized === "otherdd" ||
    normalized === "other" ||
    normalized.startsWith("other dd") ||
    normalized.includes("other dd")
  );
}

const Table_1 = ({ year, district, period, hideTableDis, assessmentStatus, assessmentStatus1, normalizedUserRole }) => {
  const [tableData, setTableData] = useState([]);
  const [chartData, setChartData] = useState({});

  const selectedYear = useMemo(() => {
    const parsed = parseInt(year, 10);
    return Number.isFinite(parsed) ? parsed : new Date().getFullYear();
  }, [year]);

  useEffect(() => {
    getAnnualActionPlan();
  }, [selectedYear, district, period]);

  async function getAnnualActionPlan() {
    try {
      if (!district) {
        setTableData([]);
        setChartData({});
        return;
      }

      const currentYear = selectedYear;
      const years = [currentYear - 2, currentYear - 1, currentYear];
      const [yearTwoLess, yearOneLess, yearNow] = years;
      const tempAllData = [];

      for (const yr of years) {
        const startDate = `${yr}-01-01`;
        const endDate = `${yr}-12-31`;

        // Planned counts still come from AAP; Executed comes from Projects & Programmes tracker.
        const [aapEntitiesResp, aapEventsResp, projectEntitiesResp, projectEventsResp] =
          await Promise.all([
            axios.get(
              `/tracker/trackedEntities?orgUnit=${district}&program=${AAP_PROGRAM}&filter=${AAP_YEAR_ATTRIBUTE}:EQ:${yr}&pageSize=5000`,
            ),
            axios.get(
              `/tracker/events?program=${AAP_PROGRAM}&orgUnit=${district}&startDate=${startDate}&endDate=${endDate}&pageSize=5000`,
            ),
            axios.get(
              `/tracker/trackedEntities?orgUnit=${district}&program=${PROJECTS_PROGRAMMES_PROGRAM}&startDate=${startDate}&endDate=${endDate}&pageSize=5000`,
            ),
            axios.get(
              `/tracker/events?program=${PROJECTS_PROGRAMMES_PROGRAM}&orgUnit=${district}&startDate=${startDate}&endDate=${endDate}&pageSize=5000`,
            ),
          ]);

        const aap = aapEntitiesResp.data.instances || [];
        const aapReports = aapEventsResp.data.instances || [];
        const projects = projectEntitiesResp.data.instances || [];
        const projectReports = projectEventsResp.data.instances || [];

        const formattedPlans = aap.map((plan, idx) => ({
          index: idx,
          dd: getAttributeValue("Development Dimension", plan),
          date: plan.createdAt,
          trackedEntity: plan.trackedEntity,
        }));

        const plannedStats = getPlanExecutionStats(formattedPlans, aapReports);

        const formattedProjects = projects.map((project, idx) => ({
          index: idx,
          dd: getAttributeValue("Development Dimension", project),
          date: project.createdAt,
          trackedEntity: project.trackedEntity,
        }));

        const executedStats = getPlanExecutionStats(
          formattedProjects,
          projectReports,
          PROJECTS_STATUS_ELEMENT,
        );

        const executedByDimension = {};
        executedStats.forEach((item) => {
          if (
            !item.dimension ||
            item.dimension === "Total" ||
            isBlankDimension(item.dimension) ||
            isOtherDdDimension(item.dimension)
          ) {
            return;
          }
          executedByDimension[item.dimension] = item.executed;
        });

        const dimensionKeys = new Set([
          ...plannedStats.map((item) => item.dimension),
          ...Object.keys(executedByDimension),
        ]);

        dimensionKeys.forEach((dimension) => {
          if (
            !dimension ||
            dimension === "Total" ||
            isBlankDimension(dimension) ||
            isOtherDdDimension(dimension)
          ) {
            return;
          }
          const plannedRow = plannedStats.find((item) => item.dimension === dimension);
          tempAllData.push({
            dimension,
            planned: plannedRow?.planned || 0,
            executed: executedByDimension[dimension] || 0,
            year: yr,
          });
        });
      }

      const grouped = {};
      const cleanedData = tempAllData.filter(
        (item) =>
          item.dimension &&
          item.dimension !== "Total" &&
          !isBlankDimension(item.dimension) &&
          !isOtherDdDimension(item.dimension),
      );

      cleanedData.forEach((item) => {
        const { dimension, planned, executed, year: itemYear } = item;

        if (!grouped[dimension]) {
          grouped[dimension] = {
            dimension,
            yearTwoLessPlan: 0,
            yearTwoLessExecuted: 0,
            yearOneLessPlan: 0,
            yearOneLessExecuted: 0,
            yearPlan: 0,
            yearExecuted: 0,
          };
        }

        if (itemYear === yearTwoLess) {
          grouped[dimension].yearTwoLessPlan += planned;
          grouped[dimension].yearTwoLessExecuted += executed;
        }

        if (itemYear === yearOneLess) {
          grouped[dimension].yearOneLessPlan += planned;
          grouped[dimension].yearOneLessExecuted += executed;
        }

        if (itemYear === yearNow) {
          grouped[dimension].yearPlan += planned;
          grouped[dimension].yearExecuted += executed;
        }
      });

      let formattedData = Object.values(grouped);

      const total = {
        dimension: "Total",
        yearTwoLessPlan: 0,
        yearTwoLessExecuted: 0,
        yearOneLessPlan: 0,
        yearOneLessExecuted: 0,
        yearPlan: 0,
        yearExecuted: 0,
      };

      formattedData.forEach((row) => {
        total.yearTwoLessPlan += row.yearTwoLessPlan;
        total.yearTwoLessExecuted += row.yearTwoLessExecuted;
        total.yearOneLessPlan += row.yearOneLessPlan;
        total.yearOneLessExecuted += row.yearOneLessExecuted;
        total.yearPlan += row.yearPlan;
        total.yearExecuted += row.yearExecuted;
      });

      formattedData = formattedData.filter((row) => row.dimension !== "Total");
      formattedData.push(total);

      formattedData.sort(
        (a, b) => DIMENSION_ORDER.indexOf(a.dimension) - DIMENSION_ORDER.indexOf(b.dimension),
      );

      setTableData(formattedData);

      const chartRows = formattedData.filter((row) => row.dimension !== "Total");

      setChartData({
        labels: chartRows.map((row) =>
          row.dimension.length > 25
            ? `${row.dimension.substring(0, 25)}...`
            : row.dimension,
        ),
        datasets: [
          {
            label: `${yearTwoLess} Planned`,
            data: chartRows.map((row) => row.yearTwoLessPlan),
            backgroundColor: "rgba(54, 162, 235, 0.6)",
          },
          {
            label: `${yearTwoLess} Executed`,
            data: chartRows.map((row) => row.yearTwoLessExecuted),
            backgroundColor: "rgba(54, 162, 235, 1)",
          },
          {
            label: `${yearOneLess} Planned`,
            data: chartRows.map((row) => row.yearOneLessPlan),
            backgroundColor: "rgba(255, 206, 86, 0.6)",
          },
          {
            label: `${yearOneLess} Executed`,
            data: chartRows.map((row) => row.yearOneLessExecuted),
            backgroundColor: "rgba(255, 206, 86, 1)",
          },
          {
            label: `${yearNow} Planned`,
            data: chartRows.map((row) => row.yearPlan),
            backgroundColor: "rgba(75, 192, 192, 0.6)",
          },
          {
            label: `${yearNow} Executed`,
            data: chartRows.map((row) => row.yearExecuted),
            backgroundColor: "rgba(75, 192, 192, 1)",
          },
        ],
      });
    } catch (err) {
      console.error("Error in getAnnualActionPlan:", err);
      setTableData([]);
      setChartData({});
    }
  }

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: `Performance Comparison by Development Dimension (${selectedYear - 2}-${selectedYear})`,
      },
    },
    scales: {
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 45,
          minRotation: 30,
        },
      },
    },
  };

  const tableMarkup = (
    <div className="table-responsive">
      <table className="table table-bordered" style={{
        border: "1px solid #000",
        borderCollapse: "collapse",
        width: "100%",
        marginTop: "20px",
      }}>
        <thead>
          <tr style={{ backgroundColor: "#d4edda", fontWeight: "bold", border: "1px solid #000" }}>
            <th style={{ border: "1px solid #000", fontWeight: "bold" }}>S/N</th>
            <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Development Dimension</th>
            <th style={{ border: "1px solid #000", fontWeight: "bold", textAlign: "center" }} colSpan="2">{selectedYear - 2}</th>
            <th style={{ border: "1px solid #000", fontWeight: "bold", textAlign: "center" }} colSpan="2">{selectedYear - 1}</th>
            <th style={{ border: "1px solid #000", fontWeight: "bold", textAlign: "center" }} colSpan="2">{selectedYear}</th>
          </tr>
          <tr style={{ fontWeight: "bold", border: "1px solid #000" }}>
            <th style={{ border: "1px solid #000" }}></th>
            <th style={{ border: "1px solid #000" }}></th>
            <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Planned</th>
            <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Executed</th>
            <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Planned</th>
            <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Executed</th>
            <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Planned</th>
            <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Executed</th>
          </tr>
        </thead>
        <tbody>
          {tableData.length > 0 ? (
            tableData.map((row, index) => (
              <tr key={index} style={row.dimension === "Total" ? { fontWeight: "bold" } : {}}>
                <td style={{ border: "1px solid #000", fontWeight: "bold" }}>{index + 1}</td>
                <td style={{ border: "1px solid #000" }}>{row.dimension}</td>
                <td style={{ border: "1px solid #000" }}>{row.yearTwoLessPlan}</td>
                <td style={{ border: "1px solid #000" }}>{row.yearTwoLessExecuted}</td>
                <td style={{ border: "1px solid #000" }}>{row.yearOneLessPlan}</td>
                <td style={{ border: "1px solid #000" }}>{row.yearOneLessExecuted}</td>
                <td style={{ border: "1px solid #000" }}>{row.yearPlan}</td>
                <td style={{ border: "1px solid #000" }}>{row.yearExecuted}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="8" style={{ textAlign: "center" }}>
                No data available for {selectedYear}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const chartMarkup = Object.keys(chartData).length > 0 ? (
    <div>
      <h5>Performance Comparison by Development Dimension ({selectedYear - 2}-{selectedYear})</h5>
      <div className="apr-chart-wrap">
        <Bar data={chartData} options={barChartOptions} />
      </div>
    </div>
  ) : null;

  return (
    <div className="col-12">
      <h3>Table 1 – Proportion of the AAP Implemented by Development Dimensions</h3>
      <div className="card">
        <div className="card-header"></div>
        <div className="card-body">
          <APRmemo
            year={year}
            districtId={district}
            tableCommentedId={`table1-${year}`}
            hideTableDis={hideTableDis}
            period={period}
          />
          <AprViewToggle
            tableContent={tableMarkup}
            chartContent={chartMarkup}
            csvFileName={`table1-aap-proportion-${selectedYear}`}
            csvHeaders={[
              "S/N",
              "Development Dimension",
              `${selectedYear - 2} Planned`,
              `${selectedYear - 2} Executed`,
              `${selectedYear - 1} Planned`,
              `${selectedYear - 1} Executed`,
              `${selectedYear} Planned`,
              `${selectedYear} Executed`,
            ]}
            csvRows={tableData.map((row, index) => [
              index + 1,
              row.dimension,
              row.yearTwoLessPlan,
              row.yearTwoLessExecuted,
              row.yearOneLessPlan,
              row.yearOneLessExecuted,
              row.yearPlan,
              row.yearExecuted,
            ])}
          />

          <APRComment
            data={tableData}
            year={year}
            districtId={district}
            tableCommentedId={`table1-${year}`}
            hideTableDis={hideTableDis}
            assessmentStatus={assessmentStatus}
            assessmentStatus1={assessmentStatus1}
            normalizedUserRole={normalizedUserRole}
          >
            {({ renderCommentInput, renderCommentList }) => (
              <>
                {renderCommentInput()}
                {renderCommentList()}
              </>
            )}
          </APRComment>
        </div>
      </div>
    </div>
  );
};

export default Table_1;
